// Degree / decimal-minute formatting for lat / lon pairs.
//
// The Assets table renders each asset's current position in "DMM"
// (degrees + decimal minutes) because that's what operators enter
// into their surface / air / subsurface UIs; DD is a bad match for
// hand-copied waypoints and DMS has too many separators for a busy
// grid column.
//
// Example: `formatDmm(41.181402, -8.703700)` → `41° 10.884' N  8° 42.222' W`.

const pad = (n: number, width: number) => n.toString().padStart(width, '0');

/// Render a single component (lat OR lon) as `D° MM.mmm' H`.
/// `isLat` picks the hemisphere letter — N/S vs E/W.
export const formatDmmComponent = (value: number, isLat: boolean): string => {
    const hemisphere = isLat
        ? (value >= 0 ? 'N' : 'S')
        : (value >= 0 ? 'E' : 'W');
    const abs = Math.abs(value);
    const degrees = Math.floor(abs);
    const minutes = (abs - degrees) * 60;
    // 3 decimal places → ~1.85 m at the equator; plenty for what an
    // operator eyeballs off the table.
    const minutesStr = minutes.toFixed(3);
    // Zero-pad the integer part of the minutes so column widths stay stable.
    const [intPart, fracPart] = minutesStr.split('.');
    const paddedMin = `${pad(Number(intPart), 2)}.${fracPart}`;
    // Latitude tops out at 90° (2 digits); longitude at 180° (3 digits).
    const degStr = isLat ? pad(degrees, 2) : pad(degrees, 3);
    return `${degStr}° ${paddedMin}' ${hemisphere}`;
};

/// Convenience wrapper for the common lat + lon pair. Returns
/// `null` when either input is null / not finite so table cells
/// render empty rather than "NaN°".
export const formatDmm = (lat: number | null | undefined, lon: number | null | undefined): string | null => {
    if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return `${formatDmmComponent(lat, true)}  ${formatDmmComponent(lon, false)}`;
};
